import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useOutletContext } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useContent } from '../contexts/ContentContext';
import {
  ChevronLeft, ChevronRight, LayoutGrid,
  Linkedin, Github, Twitter, Palette, Rocket, Zap, Box, Glasses, Megaphone, ArrowRight,
} from 'lucide-react';
import { StructuredData } from '../components/StructuredData';
import { ContactSection } from '../components/ContactSection';

type ContextType = {
  onOpenBooking: (type?: string) => void;
  onOpenContact: (subject?: string) => void;
};

declare global {
  interface Window {
    grecaptcha: any;
  }
}

export const Home: React.FC = () => {
  const location = useLocation();
  const { content, t, lang, setIsTheatreMode } = useContent();
  const { onOpenContact } = useOutletContext<ContextType>();

  const homeSettings = content.settings?.home || { projectsCount: 3, servicesCount: 6, technologiesCount: 6, teamVisibleCards: 3 };
  const sliderSettings = content.settings?.slider || { interval: 4000, translationDuration: 700 };
  const contactInfo = content.contact || { hq: {}, hubs: [] };

  const [visibleCards, setVisibleCards] = useState(homeSettings.teamVisibleCards);
  const [prevScroll, setPrevScroll] = useState(0);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setShouldLoadVideo(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    const section = document.getElementById('projects');
    if (section) observer.observe(section);
    return () => observer.disconnect();
  }, []);

  const toggleGameMode = (active: boolean) => {
    if (active) {
      const currentScrollY = window.scrollY;
      setPrevScroll(currentScrollY);

      if (currentScrollY > 50) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setTimeout(() => {
          document.body.classList.add('game-active');
          setIsTheatreMode(true);
        }, 800);
      } else {
        document.body.classList.add('game-active');
        setIsTheatreMode(true);
      }
    } else {
      setIsTheatreMode(false);
      document.body.classList.remove('game-active');
      setTimeout(() => {
        window.scrollTo({ top: prevScroll, behavior: 'smooth' });
      }, 500);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) setVisibleCards(1);
      else if (window.innerWidth < 1024) setVisibleCards(2);
      else setVisibleCards(homeSettings.teamVisibleCards);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [homeSettings.teamVisibleCards]);

  const originalTeam = content.team;
  const [currentIndex, setCurrentIndex] = useState(originalTeam.length);
  const [isPaused, setIsPaused] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const extendedTeam = [...originalTeam, ...originalTeam, ...originalTeam];
  const startIndex = originalTeam.length;

  const nextMember = useCallback(() => {
    setIsTransitioning(true);
    setCurrentIndex(prev => prev + 1);
  }, []);

  const prevMember = useCallback(() => {
    setIsTransitioning(true);
    setCurrentIndex(prev => prev - 1);
  }, []);

  useEffect(() => {
    if (currentIndex >= startIndex + originalTeam.length) {
      const timer = setTimeout(() => {
        setIsTransitioning(false);
        setCurrentIndex(startIndex);
      }, sliderSettings.transitionDuration);
      return () => clearTimeout(timer);
    }
    if (currentIndex < startIndex - (visibleCards - 1)) {
      const timer = setTimeout(() => {
        setIsTransitioning(false);
        setCurrentIndex(startIndex + originalTeam.length - (visibleCards - 1));
      }, sliderSettings.transitionDuration);
      return () => clearTimeout(timer);
    }
  }, [currentIndex, originalTeam.length, startIndex, visibleCards, sliderSettings.transitionDuration]);

  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(nextMember, sliderSettings.interval);
    return () => clearInterval(interval);
  }, [nextMember, isPaused, sliderSettings.interval]);

  const renderSocialIcon = (key: string) => {
    switch (key) {
      case 'linkedin': return <Linkedin size={14} />;
      case 'github': return <Github size={14} />;
      case 'twitter': return <Twitter size={14} />;
      case 'artstation': return <Palette size={14} />;
      default: return null;
    }
  };

  const serviceIconMap: { [key: string]: React.ReactNode } = {
    'full-cycle': <Rocket size={56} strokeWidth={1.5} />,
    'optimization': <Zap size={56} strokeWidth={1.5} />,
    'art-production': <Box size={56} strokeWidth={1.5} />,
    'xr-metaverse': <Glasses size={56} strokeWidth={1.5} />,
    'game-marketing': <Megaphone size={56} strokeWidth={1.5} />
  };

  const renderHeroTitle = () => {
    const title = t('hero_title');
    if (lang === 'en') {
      return (
        <>
          WE MAKE GREAT <br />
          <span className="text-accent">GAMES</span> FOR YOU
        </>
      );
    } else if (lang === 'bs') {
      return (
        <>
          PRAVIMO SJAJNE <br />
          <span className="text-accent">IGRE</span> ZA VAS
        </>
      );
    }
    return title;
  };

  const renderRichText = (text: string) => {
    if (!text) return null;
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i} className="font-bold text-white">{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  const getFirstSentence = (text: string) => {
    if (!text) return '';
    const match = text.match(/.*?[.!?](?:\s|$)/);
    return match ? match[0].trim() : text;
  };

  const organizationSchema = {
    "@type": "Organization",
    "name": "Saltium Games GmbH",
    "url": "https://saltiumgames.com",
    "logo": content.logo,
    "description": t('hero_subtitle'),
    "address": {
      "@type": "PostalAddress",
      "streetAddress": contactInfo?.hq?.address || 'Schäufeleinstr. 7',
      "addressLocality": contactInfo?.hq?.city || 'Munich',
      "addressCountry": contactInfo?.hq?.country || 'Germany',
      "postalCode": "80687"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": contactInfo?.phone || '+4915792601070',
      "contactType": "customer service",
      "email": contactInfo?.email || 'welcome@saltiumgames.com'
    },
    "sameAs": [
      "https://www.linkedin.com/company/saltiumgames/",
      "https://www.facebook.com/saltiumgames",
      "https://www.instagram.com/saltium.games/"
    ]
  };

  const websiteSchema = {
    "@type": "WebSite",
    "url": "https://saltiumgames.com",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://saltiumgames.com/projects?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  const sections: { [key: string]: React.ReactNode } = {
    about: (
      <section id="about" key="about" className="py-16 md:py-24 bg-[#120e16] relative overflow-hidden scroll-mt-[80px]">
        <div className="absolute top-0 left-0 w-full h-full bg-grid opacity-30"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-10 relative z-10 text-center">
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-1000">
            <h2 className="text-5xl md:text-7xl font-gaming font-bold text-white mb-8 md:mb-12 uppercase tracking-tight leading-none">
              {lang === 'en' ? <>Who <span className="text-accent">we</span> are?</> : <>Ko smo <span className="text-accent">mi?</span></>}
            </h2>
            <div className="space-y-10 text-gray-200 text-xl md:text-2xl leading-golden font-light antialiased text-center max-w-6xl mx-auto italic drop-shadow-lg opacity-80">
              <p>{renderRichText(t('aboutP1'))}</p>
              <p>{renderRichText(t('aboutP2'))}</p>
            </div>
            <div className="mt-12 md:mt-20">
              <Link id="cta-home-about-link" to="/about" className="btn-secondary">{t('nav_about')}</Link>
            </div>
          </div>
        </div>
      </section>
    ),
    services: (
      <section id="services" key="services" className="py-32 scroll-mt-[80px] bg-[#1a141f] relative overflow-hidden">
        <div className="absolute inset-0 bg-noise pointer-events-none"></div>
        <div className="absolute inset-0 bg-grid opacity-20"></div>
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="text-center mb-24 max-w-4xl mx-auto">
            <span className="text-accent font-bold uppercase tracking-[0.5em] text-xs mb-6 block">{t('nav_services')}</span>
            <h2 className="text-5xl md:text-7xl font-gaming font-extrabold text-white uppercase tracking-tighter leading-none mb-8">
              {lang === 'en' ? <>Professional <span className="text-accent">Services</span></> : <>Profesionalne <span className="text-accent">Usluge</span></>}
            </h2>
            <p className="text-lg text-gray-400 font-light max-w-2xl mx-auto leading-golden">{t('section_services_subtitle')}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {content.services.slice(0, homeSettings.servicesCount).map((service, idx) => {
              const isSpecial = service.id === 'all-services-link';
              if (isSpecial) {
                return (
                  <Link
                    key={idx}
                    to="/services"
                    className="group relative h-[500px] rounded-[40px] overflow-hidden border border-accent/40 bg-gradient-to-br from-[#1a141f] to-orange-950/20 transition-all duration-500 hover:-translate-y-2 shadow-2xl flex flex-col items-center justify-center p-12 text-center"
                  >
                    <div className="absolute inset-0 bg-grid opacity-20 group-hover:opacity-40 transition-opacity"></div>
                    <div className="relative z-10 space-y-8 flex flex-col items-center">
                      <div className="w-24 h-24 rounded-full bg-accent/10 border border-accent/30 flex items-center justify-center text-accent group-hover:scale-110 group-hover:bg-accent group-hover:text-white transition-all duration-700 shadow-[0_0_40px_rgba(249,115,22,0.3)]">
                        <LayoutGrid size={40} />
                      </div>
                      <div className="space-y-4">
                        <h3 className="text-2xl md:text-3xl font-gaming font-bold text-white uppercase tracking-tight">{t(service.title)}</h3>
                        <p className="text-gray-400 text-xs font-light max-w-[200px] padded leading-relaxed mx-auto">{t(service.description)}</p>
                      </div>
                      <div className="flex items-center gap-3 bg-white/5 border border-white/10 px-8 py-4 rounded-full group-hover:bg-accent group-hover:border-accent transition-all duration-500">
                        <span className="text-xs font-bold uppercase tracking-[0.2em] text-white">
                          {lang === 'en' ? 'Explore Expertise' : 'Istražite Ekspertizu'}
                        </span>
                        <ArrowRight size={16} className="text-accent group-hover:text-white group-hover:translate-x-2 transition-all" />
                      </div>
                    </div>
                  </Link>
                );
              }
              return (
                <Link key={idx} to={`/services/${service.id}`} id={`service-${service.id}`} className="group relative h-[500px] rounded-[40px] overflow-hidden border border-white/5 bg-[#120e16] transition-all duration-500 hover:border-accent/30 hover:-translate-y-2 shadow-2xl block">
                  <div className="absolute inset-0 z-0 overflow-hidden">
                    <img
                      loading="lazy"
                      src={service.image}
                      alt={t(service.title)}
                      width="500"
                      height="500"
                      decoding="async"
                      className="w-full h-full object-cover grayscale-[0.3] opacity-50 group-hover:grayscale-0 group-hover:opacity-80 group-hover:scale-110 transition-all duration-1000 ease-out"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#120e16] via-[#120e16]/30 to-transparent"></div>
                  </div>
                  <div className="relative z-10 h-full p-10 flex flex-col justify-end">
                    <div className="absolute top-10 left-10 text-accent/10 group-hover:text-accent/30 transition-all transform group-hover:-translate-y-2 duration-700">
                      {serviceIconMap[service.id] || <Box size={56} />}
                    </div>
                    <div className="relative">
                      <h3 className="text-2xl md:text-3xl font-gaming font-bold text-white mb-4 uppercase tracking-tight leading-tight group-hover:text-accent transition-colors">{t(service.title)}</h3>
                      <p className="text-gray-200 text-sm leading-golden font-medium mb-8 line-clamp-3 group-hover:line-clamp-none transition-all duration-500 antialiased drop-shadow-md">
                        {getFirstSentence(t(service.cardDescription || service.description))}
                      </p>
                      <div className="inline-flex items-center gap-3 text-accent font-black uppercase tracking-[0.2em] text-xs transition-all">
                        {t('learn_more')}
                        <span className="group-hover:translate-x-3 transition-transform duration-300">→</span>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    ),
    technologies: (
      <section id="technologies" key="technologies" className="py-32 scroll-mt-[80px] bg-[#120e16] relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-30"></div>
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-20 gap-8">
            <div>
              <span className="text-accent font-bold uppercase tracking-[0.4em] text-xs mb-4 block">{t('nav_tech')}</span>
              <h2 className="text-5xl md:text-7xl font-gaming font-bold text-white uppercase tracking-tighter leading-none">
                {lang === 'en' ? <>Engineered for <span className="text-accent">Quality</span></> : <>Dizajnirano za <span className="text-accent">Kvalitet</span></>}
              </h2>
            </div>
            <Link id="cta-home-tech-link" to="/technologies" className="btn-secondary">{t('tech_all_btn')}</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            {content.technologies.slice(0, homeSettings.technologiesCount).map((tech) => (
              <Link to={`/technologies/${tech.id}`} key={tech.name} id={`tech-${tech.name.replace(/\s+/g, '-')}`} className="group p-7 bg-[#1a141f] border border-white/5 rounded-[40px] hover:border-accent/40 transition-all hover:-translate-y-2 relative overflow-hidden shadow-2xl flex flex-col h-full">
                <div className="absolute -top-10 -right-10 w-24 h-24 bg-accent opacity-0 group-hover:opacity-10 blur-[50px] rounded-full transition-opacity duration-700"></div>
                <div className="mb-7 grayscale-[0.8] group-hover:grayscale-0 transition-all transform group-hover:scale-110 duration-500 drop-shadow-lg w-12 h-12 md:w-16 md:h-16 flex items-center justify-center">
                  <img src={tech.icon} alt={tech.name} className="w-full h-full object-contain" />
                </div>
                <div className="flex-grow">
                  <h3 className="text-xl font-gaming font-bold text-white mb-4 uppercase tracking-tight group-hover:text-accent transition-colors leading-tight">{tech.name}</h3>
                  <p className="text-gray-400 text-xs font-light leading-golden mb-8">{t(tech.shortDescription)}</p>
                </div>
                <div className="flex items-center gap-2 text-accent font-bold text-[10px] uppercase tracking-widest transition-all mt-auto pt-5 border-t border-white/5">
                  {t('learn_more')}
                  <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    ),
    platforms: (
      <section id="platforms" key="platforms" className="py-24 border-y border-white/5 bg-[#1a141f] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <p className="text-center text-gray-400 font-bold uppercase tracking-[0.5em] text-xs mb-20 opacity-80">{t('section_platforms_label')}</p>
          <div className="flex flex-wrap lg:flex-nowrap justify-center gap-x-8 md:gap-x-12 lg:gap-x-16 gap-y-12 items-center overflow-x-auto lg:overflow-visible no-scrollbar pb-4 md:pb-0">
            {content.platforms.map((platform, i) => (
              <div key={i} className="flex items-center justify-center group transition-all duration-500 cursor-default shrink-0">
                <img
                  loading="lazy"
                  src={platform.icon}
                  alt={platform.name}
                  title={platform.name}
                  width="112"
                  height="112"
                  decoding="async"
                  className="h-[60px] md:h-20 lg:h-28 w-auto object-contain brightness-0 invert opacity-40 group-hover:brightness-100 group-hover:invert-0 group-hover:opacity-100 transition-all duration-500 transform group-hover:scale-110"
                />
              </div>
            ))}
          </div>
        </div>
      </section>
    ),
    projects: (
      <section id="projects" key="projects" className="py-32 bg-[#120e16] scroll-mt-[80px] relative overflow-hidden">
        {shouldLoadVideo && (
          <video autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover opacity-20 grayscale-[0.5] pointer-events-none animate-in fade-in duration-[2000ms]">
            <source src="https://saltiumgamesdev.com/projects.mp4" type="video/mp4" />
          </video>
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-[#120e16] via-transparent to-[#120e16] z-[1]"></div>
        <div className="absolute inset-0 bg-grid opacity-30 z-[1]"></div>
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-20 gap-8">
            <div>
              <span className="text-accent font-bold uppercase tracking-[0.4em] text-xs mb-4 block">{t('nav_projects')}</span>
              <h2 className="text-5xl md:text-7xl font-gaming font-bold text-white uppercase tracking-tighter leading-none">
                {lang === 'en' ? <>Our <span className="text-accent">Projects</span></> : <>Naši <span className="text-accent">Projekti</span></>}
              </h2>
            </div>
            <Link id="cta-home-projects-link" to="/projects" className="btn-secondary">{t('projects_all_btn')}</Link>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {content.projects.slice(0, homeSettings.projectsCount).map((project) => (
              <Link to={`/projects/${project.id}`} key={project.id} id={`project-${project.id}`} className="group bg-[#1a141f]/80 backdrop-blur-sm border border-white/5 rounded-[40px] overflow-hidden hover:border-accent transition-all shadow-2xl flex flex-col">
                <div className="aspect-[16/10] overflow-hidden relative">
                  <img
                    loading="lazy"
                    src={project.image}
                    alt={t(project.title)}
                    width="600"
                    height="375"
                    decoding="async"
                    className="w-full h-full object-cover grayscale-[0.4] group-hover:grayscale-0 transition-all duration-700 transform group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0d0a0f] via-transparent to-transparent opacity-80"></div>
                </div>
                <div className="p-10 flex-grow flex flex-col">
                  <h3 className="text-2xl font-gaming font-bold uppercase tracking-tight text-white mb-4 group-hover:text-accent transition-colors">{t(project.title)}</h3>
                  <p className="text-gray-300 text-sm leading-golden line-clamp-3 font-light mb-8 antialiased">{t(project.description)}</p>
                  <div className="mt-auto flex items-center gap-3 text-accent font-black uppercase tracking-[0.2em] text-xs">
                    {t('view_details')} <span className="group-hover:translate-x-3 transition-transform duration-300">→</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    ),
    team: (
      <section id="team" key="team" className="py-20 scroll-mt-[80px] relative overflow-hidden bg-[#1a141f]">
        <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="mb-12">
            <span className="text-accent font-bold uppercase tracking-[0.5em] text-xs mb-6 block">{t('nav_team')}</span>
            <h2 className="text-5xl md:text-7xl font-gaming font-bold text-white uppercase tracking-tighter leading-none">
              {lang === 'en' ? <>The <span className="text-accent">Minds</span> Behind</> : <>Mozgovi <span className="text-accent">Iza</span> Projekta</>}
            </h2>
          </div>
          <div className="relative group" onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)}>
            <button
              onClick={prevMember}
              className="absolute left-2 sm:left-4 md:-left-16 top-1/2 -translate-y-1/2 z-40 w-12 h-12 rounded-full bg-black/50 border border-white/10 flex items-center justify-center text-white hover:bg-accent hover:border-accent hover:border-accent transition-all shadow-3xl backdrop-blur-xl"
              aria-label={lang === 'en' ? "Previous team member" : "Prethodni član tima"}
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={nextMember}
              className="absolute right-2 sm:right-4 md:-right-16 top-1/2 -translate-y-1/2 z-40 w-12 h-12 rounded-full bg-black/50 border border-white/10 flex items-center justify-center text-white hover:bg-accent hover:border-accent hover:border-accent transition-all shadow-3xl backdrop-blur-xl"
              aria-label={lang === 'en' ? "Next team member" : "Sljedeći član tima"}
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            <div className="overflow-hidden py-8">
              <div className={`flex ${isTransitioning ? 'transition-transform duration-700' : 'transition-none'}`} style={{ transform: `translateX(-${(currentIndex * 100) / visibleCards}%)` }}>
                {extendedTeam.map((member, idx) => (
                  <div key={`${member.name}-${idx}`} className="min-w-[100%] sm:min-w-[50%] lg:min-w-[33.333%] px-4">
                    <div className="group/card relative bg-[#120e16] border border-white/5 rounded-[40px] p-8 h-full transition-all duration-500 hover:-translate-y-2 shadow-3xl flex flex-col items-center text-center overflow-hidden">
                      <div className="relative mb-6 z-10">
                        <div className="absolute inset-0 bg-accent/40 rounded-full blur-2xl opacity-0 group-hover/card:opacity-100 transition-opacity duration-500 scale-125"></div>
                        <img
                          loading="lazy"
                          src={member.avatar}
                          alt={member.name}
                          width="128"
                          height="128"
                          decoding="async"
                          className="relative z-20 w-32 h-32 rounded-full border-2 border-[#1a141f] object-cover grayscale opacity-80 group-hover/card:grayscale-0 group-hover/card:opacity-100 transition-all duration-500 group-hover/card:border-accent shadow-lg"
                        />
                      </div>
                      <h3 className="text-2xl font-gaming font-bold text-white uppercase tracking-tight group-hover/card:text-accent transition-colors duration-500">{member.name}</h3>
                      <p className="text-accent font-semibold text-xs uppercase opacity-70 mb-3">{t(member.role)}</p>
                      <p className="text-gray-400 text-xs leading-golden italic font-light line-clamp-2 antialiased">{t(member.quote)}</p>
                      <div className="mt-4 flex gap-3 opacity-0 translate-y-2 group-hover/card:opacity-100 group-hover/card:translate-y-0 transition-all duration-500">
                        {(Object.entries(member.social || {}).map(([key, val]) => (
                          val ? (
                            <a key={key} href={val as string} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-gray-400 hover:text-white hover:bg-accent transition-all" aria-label={`${member.name} ${key}`}>
                              {renderSocialIcon(key)}
                            </a>
                          ) : null
                        )) as React.ReactNode[])}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-8 text-center">
            <Link id="cta-home-team-link" to="/team" className="btn-secondary py-3 px-10 text-xs">{t('meet_team')}</Link>
          </div>
        </div>
      </section>
    )
  };

  return (
    <div className="relative">
      <StructuredData data={organizationSchema} />
      <StructuredData data={websiteSchema} />
      <section id="hero" className="relative h-screen flex flex-col items-center justify-center text-center px-4">
        <div className="max-w-4xl relative z-20 animate-in fade-in zoom-in duration-1000 mt-[10vh]">
          <span className="text-accent font-bold uppercase tracking-[0.6em] text-xs mb-8 block drop-shadow-lg label-glow">{t('hero_label')}</span>
          <h1 className="text-6xl md:text-8xl font-gaming font-extrabold text-white mb-10 uppercase tracking-tighter leading-[0.9] accent-glow drop-shadow-2xl">
            {renderHeroTitle()}
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 mb-12 font-light leading-golden max-w-xl mx-auto italic drop-shadow-lg opacity-80 antialiased">{t('hero_subtitle')}</p>
          <div className="flex items-center justify-center">
            <button id="cta-home-hero-play" onClick={() => toggleGameMode(true)} className="btn-primary">{t('hero_btn')}</button>
          </div>
        </div>
      </section>

      <div className="relative z-10">
        {(content.sectionOrder.map((id: string) => (
          <React.Fragment key={id}>{sections[id] || null}</React.Fragment>
        )) as React.ReactNode[])}

        <ContactSection id="contact" />
      </div>
    </div>
  );
};
